import { Injectable, computed, signal } from '@angular/core';
import { setLogLevel } from '@angular/fire/firestore';
import { environment } from '../../../environments/environment';

/**
 * Canonical categories of Firestore activity we can observe.
 *
 * We capture events from two independent sources:
 *
 * 1. SDK debug log interception (primary, accurate). We enable Firestore's
 *    built-in debug logs via `setLogLevel('debug')` and intercept
 *    `console.log`/`console.debug` to classify every internal SDK message.
 *    This sees operations at the correct level: target subscriptions,
 *    document deliveries, write submissions, and transaction commits.
 *
 * 2. Fetch interception (secondary, channel-level). We monkey-patch
 *    `window.fetch` to count HTTP requests to the Firestore endpoints.
 *    Useful for detecting REST calls and correlating channel resume
 *    cycles, but cannot count individual document reads inside gRPC-Web
 *    streams.
 */
export type FsCategory =
  // --- SDK-level (from debug logs) ---
  | 'listen_target_add' // A new query listener was attached
  | 'listen_target_remove' // A listener was unsubscribed
  | 'watch_change' // Watch stream delivered document change(s) for a listener
  | 'document_read' // One-shot document read (getDoc/getDocs)
  | 'document_write' // Local write submitted to the write stream
  | 'write_ack' // Server acknowledged a write
  | 'transaction' // Transaction commit/retry
  | 'sdk_other' // Other SDK log we couldn't classify
  // --- Fetch-level (from window.fetch monkey-patch) ---
  | 'http_batchget' // :batchGet REST call
  | 'http_commit' // :commit REST call
  | 'http_runquery' // :runQuery REST call
  | 'http_runaggregation' // :runAggregationQuery REST call
  | 'http_listen_channel' // Listen/channel fetch (gRPC-Web stream HTTP POST)
  | 'http_write_channel'; // Write/channel fetch (gRPC-Web stream HTTP POST)

export interface FsEvent {
  category: FsCategory;
  source: 'sdk-log' | 'fetch';
  message?: string;
  path?: string;
  targetId?: number;
  timestamp: number;
  durationMs?: number;
}

/**
 * Global Firestore debug interceptor.
 *
 * Combines two observation techniques to give visibility into what the
 * Firestore SDK is actually doing:
 *
 * - **SDK debug log parsing**: enables `setLogLevel('debug')` and captures
 *   every Firestore log message. Each message is classified into a
 *   canonical category (listener add/remove, watch change, document
 *   read/write, write ack, transaction). This is the primary source and
 *   gives operation-level accuracy.
 *
 * - **Fetch interception**: monkey-patches `window.fetch` to additionally
 *   count HTTP requests to Firestore endpoints. This catches REST calls
 *   that bypass the SDK's gRPC-Web channels and shows channel open/resume
 *   cycles.
 *
 * Enable at runtime by setting `localStorage.firestoreDebug = 'true'` and
 * reloading, or by calling `__fsDebug.enable()` from the browser console.
 *
 * From the browser console:
 * ```
 *   __fsDebug.enable()           // start observing
 *   __fsDebug.reset()            // clear counters
 *   __fsDebug.report()           // print a summary table
 *   __fsDebug.countByCategory()  // raw counts
 *   __fsDebug.events()           // full event log
 *   __fsDebug.disable()          // stop observing
 * ```
 */
@Injectable({ providedIn: 'root' })
export class FirestoreDebugService {
  private eventsSignal = signal<FsEvent[]>([]);
  private enabledSignal = signal(false);

  // Fetch interception state
  private originalFetch?: typeof window.fetch;

  // Console interception state
  private originalConsoleLog?: typeof console.log;
  private originalConsoleDebug?: typeof console.debug;
  private originalConsoleInfo?: typeof console.info;

  /** Readonly list of all captured events since the last reset. */
  readonly events = this.eventsSignal.asReadonly();

  /** Whether observation is currently active. */
  readonly enabled = this.enabledSignal.asReadonly();

  /** Total number of captured events. */
  readonly total = computed(() => this.eventsSignal().length);

  /** Count of captured events by category. */
  readonly countByCategory = computed(() => {
    const counts: Record<FsCategory, number> = {
      listen_target_add: 0,
      listen_target_remove: 0,
      watch_change: 0,
      document_read: 0,
      document_write: 0,
      write_ack: 0,
      transaction: 0,
      sdk_other: 0,
      http_batchget: 0,
      http_commit: 0,
      http_runquery: 0,
      http_runaggregation: 0,
      http_listen_channel: 0,
      http_write_channel: 0,
    };
    for (const ev of this.eventsSignal()) {
      counts[ev.category]++;
    }
    return counts;
  });

  /**
   * Enable both the SDK debug log interceptor and the fetch interceptor.
   * Safe to call multiple times — no-op if already enabled.
   */
  enable(): void {
    if (this.enabledSignal()) return;
    if (typeof window === 'undefined') return;

    this.enableFetchInterception();
    this.enableSdkLogInterception();
    this.enabledSignal.set(true);

    // eslint-disable-next-line no-console
    console.log(
      '%c[FirestoreDebug] Enabled',
      'color: #2196f3; font-weight: bold',
      '— SDK log + fetch interceptors active. Use __fsDebug.report() for a summary.'
    );
  }

  /** Disable observation. Restores fetch and console originals. */
  disable(): void {
    if (!this.enabledSignal()) return;
    this.disableFetchInterception();
    this.disableSdkLogInterception();
    this.enabledSignal.set(false);
    // eslint-disable-next-line no-console
    console.log('%c[FirestoreDebug] Disabled', 'color: #757575');
  }

  /** Clear all captured events and reset counters. */
  reset(): void {
    this.eventsSignal.set([]);
    // eslint-disable-next-line no-console
    console.log('%c[FirestoreDebug] Reset', 'color: #757575');
  }

  /** Print a summary table to the console. */
  report(): void {
    const counts = this.countByCategory();
    const total = this.total();

    // Group counts by source for a cleaner summary
    const sdkGroup = {
      'listen target add': counts.listen_target_add,
      'listen target remove': counts.listen_target_remove,
      'watch change': counts.watch_change,
      'document read': counts.document_read,
      'document write': counts.document_write,
      'write ack': counts.write_ack,
      transaction: counts.transaction,
      'sdk other': counts.sdk_other,
    };
    const httpGroup = {
      ':batchGet': counts.http_batchget,
      ':commit': counts.http_commit,
      ':runQuery': counts.http_runquery,
      ':runAggregationQuery': counts.http_runaggregation,
      'Listen/channel': counts.http_listen_channel,
      'Write/channel': counts.http_write_channel,
    };

    // eslint-disable-next-line no-console
    console.log(
      `%c[FirestoreDebug] Summary — ${total} event(s)`,
      'color: #2196f3; font-weight: bold'
    );
    // eslint-disable-next-line no-console
    console.log('%cSDK-level operations (from debug logs):', 'font-weight: bold');
    // eslint-disable-next-line no-console
    console.table(sdkGroup);
    // eslint-disable-next-line no-console
    console.log('%cHTTP-level operations (from fetch):', 'font-weight: bold');
    // eslint-disable-next-line no-console
    console.table(httpGroup);
  }

  // --- Internal recording ---

  private recordEvent(ev: FsEvent): void {
    this.eventsSignal.update(arr => [...arr, ev]);
  }

  // --- Fetch interception ---

  private enableFetchInterception(): void {
    if (!window.fetch) return;

    this.originalFetch = window.fetch.bind(window);
    const original = this.originalFetch;
    const self = this;

    window.fetch = function patchedFetch(
      input: RequestInfo | URL,
      init?: RequestInit
    ): Promise<Response> {
      const url = self.resolveUrl(input);
      if (!self.isFirestoreUrl(url)) {
        return original(input, init);
      }

      const start = performance.now();
      const category = self.classifyHttpCategory(url);
      const path = self.extractPathFromBody(category, init);

      return original(input, init).then(
        response => {
          self.recordEvent({
            category,
            source: 'fetch',
            path,
            message: url.split('?')[0],
            timestamp: Date.now(),
            durationMs: performance.now() - start,
          });
          return response;
        },
        err => {
          self.recordEvent({
            category,
            source: 'fetch',
            path,
            message: `ERROR ${url.split('?')[0]}`,
            timestamp: Date.now(),
            durationMs: performance.now() - start,
          });
          throw err;
        }
      );
    };
  }

  private disableFetchInterception(): void {
    if (this.originalFetch) {
      window.fetch = this.originalFetch;
      this.originalFetch = undefined;
    }
  }

  private resolveUrl(input: RequestInfo | URL): string {
    if (typeof input === 'string') return input;
    if (input instanceof URL) return input.href;
    return input.url;
  }

  private isFirestoreUrl(url: string): boolean {
    if (url.includes('firestore.googleapis.com')) return true;
    const fs = environment.emulators?.firestore;
    if (fs) {
      const host = fs.host === '0.0.0.0' ? '127.0.0.1' : fs.host;
      if (url.includes(`//${host}:${fs.port}/`)) return true;
      // Match localhost/127.0.0.1 interchangeably so the counter works
      // regardless of which form the SDK happens to emit.
      if (host === '127.0.0.1' && url.includes(`//localhost:${fs.port}/`)) return true;
      if (host === 'localhost' && url.includes(`//127.0.0.1:${fs.port}/`)) return true;
    }
    return false;
  }

  private classifyHttpCategory(url: string): FsCategory {
    // Match both the REST format (":methodName") and the gRPC-Web format
    // ("/MethodName"). The Firestore JS SDK uses gRPC-Web, so the REST-only
    // checks here silently failed before — aggregation calls never matched,
    // and the `http_runaggregation` counter always stayed at 0.
    //
    // Aggregation MUST be checked before runQuery — "runAggregationQuery"
    // and "RunAggregationQuery" don't contain "runQuery"/"RunQuery" as a
    // substring (the 4th char differs: A vs Q), but keeping aggregation
    // first is defensive in case the URL format changes.
    if (/[:/]batchGet(Documents)?\b/i.test(url)) return 'http_batchget';
    if (/[:/]commit\b/i.test(url)) return 'http_commit';
    if (/[:/]runAggregationQuery\b/i.test(url)) return 'http_runaggregation';
    if (/[:/]runQuery\b/i.test(url)) return 'http_runquery';
    if (url.includes('/Listen/channel')) return 'http_listen_channel';
    if (url.includes('/Write/channel')) return 'http_write_channel';
    return 'sdk_other';
  }

  private extractPathFromBody(category: FsCategory, init?: RequestInit): string | undefined {
    if (!init?.body || typeof init.body !== 'string') return undefined;
    try {
      const body = JSON.parse(init.body);
      if (category === 'http_batchget' && Array.isArray(body.documents)) {
        return body.documents.map((d: string) => this.stripFirestorePrefix(d)).join(', ');
      }
      if (category === 'http_commit' && Array.isArray(body.writes)) {
        return body.writes
          .map((w: { update?: { name?: string }; delete?: string }) => {
            const name = w.update?.name ?? w.delete;
            return name ? this.stripFirestorePrefix(name) : undefined;
          })
          .filter(Boolean)
          .join(', ');
      }
      if (category === 'http_runquery' || category === 'http_runaggregation') {
        return body.parent ? this.stripFirestorePrefix(body.parent) : undefined;
      }
    } catch {
      // Not JSON — that's fine for gRPC-Web channel requests
    }
    return undefined;
  }

  private stripFirestorePrefix(name: string): string {
    return name.replace(/^projects\/[^/]+\/databases\/[^/]+\/documents\/?/, '');
  }

  // --- SDK log interception ---

  private enableSdkLogInterception(): void {
    // Enable Firestore's built-in verbose logging
    setLogLevel('debug');

    const self = this;

    // Firestore logs go through multiple console methods depending on level
    this.originalConsoleLog = console.log.bind(console);
    this.originalConsoleDebug = console.debug.bind(console);
    this.originalConsoleInfo = console.info.bind(console);

    const intercept = (original: (...args: unknown[]) => void) => {
      return function patched(...args: unknown[]): void {
        // Only consider messages that look like Firestore SDK output
        const first = args[0];
        if (typeof first === 'string' && self.isFirestoreSdkLog(first)) {
          self.classifyAndRecordSdkLog(args);
        }
        original(...args);
      };
    };

    console.log = intercept(this.originalConsoleLog);
    console.debug = intercept(this.originalConsoleDebug);
    console.info = intercept(this.originalConsoleInfo);
  }

  private disableSdkLogInterception(): void {
    if (this.originalConsoleLog) {
      console.log = this.originalConsoleLog;
      this.originalConsoleLog = undefined;
    }
    if (this.originalConsoleDebug) {
      console.debug = this.originalConsoleDebug;
      this.originalConsoleDebug = undefined;
    }
    if (this.originalConsoleInfo) {
      console.info = this.originalConsoleInfo;
      this.originalConsoleInfo = undefined;
    }
    // Revert to silent logging
    setLogLevel('silent');
  }

  private isFirestoreSdkLog(message: string): boolean {
    return message.includes('@firebase/firestore') || /Firestore \(\d+\.\d+\.\d+\)/.test(message);
  }

  private classifyAndRecordSdkLog(args: unknown[]): void {
    const msg = args.map(a => (typeof a === 'string' ? a : JSON.stringify(a))).join(' ');

    // Extract path and targetId up-front (before any truncation) so events
    // always carry them even if the stored message is clipped.

    // -- Listener subscription: just check if the payload contains "addTarget":{
    //    Real SDK log shape:
    //      "RPC 'Listen' stream 0x... sending: {"database":"...","addTarget":{"query":{...},"targetId":N}}"
    //    Don't use [^}]*? — addTarget contains nested objects with their own
    //    closing braces, so the non-greedy skip dies too early. Just check
    //    presence, then extract targetId separately with a free-standing match.
    if (msg.includes('"addTarget":{')) {
      const targetId = this.extractTargetId(msg);
      const path = this.extractTargetPath(msg);
      this.recordEvent({
        category: 'listen_target_add',
        source: 'sdk-log',
        message: msg.length > 600 ? msg.slice(0, 600) + '…' : msg,
        path,
        targetId,
        timestamp: Date.now(),
      });
      return;
    }

    // -- Listener unsubscription: JSON "removeTarget" payload (integer value) --
    const removeTargetMatch = msg.match(/"removeTarget"\s*:\s*(\d+)/);
    if (removeTargetMatch) {
      const targetId = parseInt(removeTargetMatch[1], 10);
      this.recordEvent({
        category: 'listen_target_remove',
        source: 'sdk-log',
        message: msg.length > 600 ? msg.slice(0, 600) + '…' : msg,
        targetId,
        timestamp: Date.now(),
      });
      return;
    }

    // -- Watch stream document delivery: be strict and only count actual
    //    document change envelopes, not target/filter state changes. --
    if (/"documentChange"|"documentDelete"|"documentRemove"/.test(msg)) {
      this.recordEvent({
        category: 'watch_change',
        source: 'sdk-log',
        message: msg.length > 600 ? msg.slice(0, 600) + '…' : msg,
        timestamp: Date.now(),
      });
      return;
    }

    // -- One-shot document reads from BatchGetDocuments RPC --
    if (/BatchGetDocuments|RPC 'BatchGetDocuments'/.test(msg)) {
      this.recordEvent({
        category: 'document_read',
        source: 'sdk-log',
        message: msg.length > 600 ? msg.slice(0, 600) + '…' : msg,
        timestamp: Date.now(),
      });
      return;
    }

    // -- Write submission (before server ack) --
    if (/RPC 'Commit'|WriteStream.*sending|"writes"\s*:/.test(msg)) {
      this.recordEvent({
        category: 'document_write',
        source: 'sdk-log',
        message: msg.length > 600 ? msg.slice(0, 600) + '…' : msg,
        timestamp: Date.now(),
      });
      return;
    }

    // -- Write acknowledgment from the server --
    if (/"writeResults"|"commitTime"|WriteStream.*received/.test(msg)) {
      this.recordEvent({
        category: 'write_ack',
        source: 'sdk-log',
        message: msg.length > 600 ? msg.slice(0, 600) + '…' : msg,
        timestamp: Date.now(),
      });
      return;
    }

    // -- Firestore transactions (not internal memory persistence transactions) --
    if (/RPC 'BeginTransaction'|RPC 'Rollback'/.test(msg)) {
      this.recordEvent({
        category: 'transaction',
        source: 'sdk-log',
        message: msg.length > 600 ? msg.slice(0, 600) + '…' : msg,
        timestamp: Date.now(),
      });
      return;
    }

    // -- Anything else from the SDK (memory transactions, stream lifecycle, etc.) --
    this.recordEvent({
      category: 'sdk_other',
      source: 'sdk-log',
      message: msg.length > 300 ? msg.slice(0, 300) + '…' : msg,
      timestamp: Date.now(),
    });
  }

  /** Extract the targetId from an addTarget payload. The payload contains
   *  nested objects, so we match `"targetId":N` anywhere in the string —
   *  it's unambiguous since Firestore only ever emits one targetId per
   *  addTarget message. */
  private extractTargetId(msg: string): number | undefined {
    const m = msg.match(/"targetId"\s*:\s*(\d+)/);
    return m ? parseInt(m[1], 10) : undefined;
  }

  /** Extract the collection or query path from an addTarget JSON payload. */
  private extractTargetPath(msg: string): string | undefined {
    // Try documents watch: "documents":{"documents":["projects/.../documents/PATH"]}
    const docsMatch = msg.match(/"documents"\s*:\s*\[\s*"([^"]+)"/);
    if (docsMatch) return this.stripFirestorePrefix(docsMatch[1]);

    // Try structured query: "query":{"parent":"projects/.../documents/PATH"}
    const queryMatch = msg.match(/"parent"\s*:\s*"([^"]+)"/);
    if (queryMatch) {
      const parent = this.stripFirestorePrefix(queryMatch[1]);
      // Also try to grab the collectionId from structuredQuery.from
      const fromMatch = msg.match(/"from"\s*:\s*\[\s*\{\s*"collectionId"\s*:\s*"([^"]+)"/);
      if (fromMatch) return parent ? `${parent}/${fromMatch[1]}` : fromMatch[1];
      return parent;
    }

    return undefined;
  }
}
